'use client';

import { usePathname, useRouter } from 'next/navigation';
import { useState } from 'react';
import ActionButton from '../../../components/form/ActionButton/ActionButton';
import LotteryDrawButton from '../../../features/lottery/components/LotteryDrawButton/LotteryDrawButton';
import type { LotteryTradeMyGoods, TradeDesiredGoods } from '../../../features/lottery/types';
import utilsStyles from '../../../styles/modules/utils.module.scss';
import { newDate } from '../../../utils/date';
import ModalBase from '../ModalBase/ModalBase';
import { useTradeContext } from '../TradePageClient/TradePageClient';
import styles from './TradeBar.module.scss';

// TradeDesiredGoodsかどうかを判定する型ガード
function isTradeDesiredGoods(data: LotteryTradeMyGoods | TradeDesiredGoods): data is TradeDesiredGoods {
  return 'hashKey' in data;
}

export type TradeBarProps = {
  goods?: LotteryTradeMyGoods | TradeDesiredGoods;
  lotteryId: string;
  hashKey?: string;
  tradeRequestId?: string;
  tradeAt?: string;
};

function TradeBar({ goods, lotteryId, hashKey, tradeRequestId, tradeAt }: TradeBarProps) {
  const { selectedItems } = useTradeContext();
  const router = useRouter();
  const pathname = usePathname();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const totalCount = Array.from(selectedItems.values()).reduce((sum, current) => sum + current, 0);

  // 現在のパスに基づいてボタンの表示を制御
  const isSelectTradeablePrizesPage = pathname === `/trade/${lotteryId}/select-tradeable-prizes`;
  const isSelectDesiredPrizesPage = pathname === `/trade/${lotteryId}/select-desired-prizes`;
  const isConfirmPage = pathname === `/trade/${lotteryId}/confirm`;
  const isHistoryDetailPage =
    pathname.includes(`/trade/${lotteryId}/history/`) &&
    !pathname.includes('/cancelComplete') &&
    pathname.split('/').length === 5; // /trade/[lotteryId]/history/[tradeRequestId]

  // /select-desired-prizesページでの残り個数計算
  const calculateRemainingCount = () => {
    if (!goods) return 0;

    if (isSelectDesiredPrizesPage && isTradeDesiredGoods(goods)) {
      // TradeDesiredGoodsの場合：各賞のtradeableInCountの合計から totalCount を引く
      const totalTradeableInCount = goods.labels.reduce((sum, label) => {
        return sum + (label.tradeableInCount || 0);
      }, 0);
      return Math.max(0, totalTradeableInCount - totalCount);
    }
    // LotteryTradeMyGoodsの場合：従来の計算方法
    return Math.max(0, (goods?.minTradeRequiredCount || 0) - totalCount);
  };

  const remainingCount = calculateRemainingCount();

  // 交換可能期間内かどうかを判定
  const isWithinTradePeriod = (tradeAt: string | undefined): boolean => {
    if (!tradeAt) return false;

    try {
      const tradeDate = newDate(tradeAt);
      const now = newDate();
      return now <= tradeDate;
    } catch {
      return false;
    }
  };

  // 取り消し可能かどうかの判定
  const canCancel = isWithinTradePeriod(tradeAt);

  // select-tradeable-prizesページ用: ラベルごとの選択数がminTradeRequiredCountを満たしているか検証
  const hasInsufficientLabelOnTradeablePage = (() => {
    if (!goods || !isSelectTradeablePrizesPage) return false;

    const minRequired = goods.minTradeRequiredCount || 0;

    // 何も選択されていない場合は非活性
    if (totalCount === 0) return true;

    // ラベルごとに選択数を集計
    for (const label of goods.labels) {
      let labelSelectedCount = 0;
      for (const good of label.goods || []) {
        const count = selectedItems.get(good.id.toString()) || 0;
        labelSelectedCount += count;
      }
      // そのラベルで1つ以上選択されているのに、必要数未満なら不足扱い
      if (labelSelectedCount > 0 && labelSelectedCount < minRequired) {
        return true;
      }
    }
    return false;
  })();

  const handleNavigateToDesiredPrizes = () => {
    if (!goods) return;

    // 選択された賞を特定
    const selectedLabels = new Set<string>();
    Array.from(selectedItems.entries())
      .filter(([, count]) => count > 0)
      .forEach(([goodId]) => {
        // goodsデータから該当するラベルを特定
        for (const label of goods.labels) {
          const foundGood = label.goods?.find((good) => good.id.toString() === goodId);
          if (foundGood) {
            selectedLabels.add(label.stagingKey);
            break;
          }
        }
      });

    // 選択されたアイテムの詳細情報を含めて保存
    const selectedItemsWithDetails = Array.from(selectedItems.entries())
      .filter(([, count]) => count > 0)
      .map(([goodId, count]) => {
        // goodsデータから詳細情報を取得
        let goodDetails = null;
        for (const label of goods.labels) {
          const foundGood = label.goods?.find((good) => good.id.toString() === goodId);
          if (foundGood) {
            goodDetails = {
              id: foundGood.id,
              name: foundGood.name,
              imageUrl: foundGood.imageUrl,
              labelName: label.name,
              stagingKey: label.stagingKey,
              stagingName: label.stagingName,
            };
            break;
          }
        }

        return {
          lottery_prize_goods_id: Number(goodId),
          count: count,
          details: goodDetails,
        };
      });

    // 選択された賞の全ての景品情報を保存
    const allGoodsForSelectedLabels = goods.labels
      .filter((label) => selectedLabels.has(label.stagingKey))
      .flatMap(
        (label) =>
          label.goods?.map((good) => ({
            id: good.id,
            name: good.name,
            imageUrl: good.imageUrl,
            acquiredCount: good.acquiredCount,
            personalizeFlag: good.personalizeFlag,
            labelName: label.name,
            stagingKey: label.stagingKey,
            stagingName: label.stagingName,
            minTradeRequiredCount: goods.minTradeRequiredCount,
          })) || [],
      );

    const selectedItemsArray = selectedItemsWithDetails.map((item) => ({
      lottery_prize_goods_id: item.lottery_prize_goods_id,
      count: item.count,
    }));

    try {
      // sessionStorageに詳細情報も保存
      const storageKey = `tradeData_${lotteryId}_${Date.now()}`;
      const detailsKey = `tradeDetails_${lotteryId}_${Date.now()}`;
      const allGoodsKey = `allGoods_${lotteryId}_${Date.now()}`;

      sessionStorage.setItem(storageKey, JSON.stringify(selectedItemsArray));
      sessionStorage.setItem(detailsKey, JSON.stringify(selectedItemsWithDetails));
      sessionStorage.setItem(allGoodsKey, JSON.stringify(allGoodsForSelectedLabels));

      // 手動でページ遷移
      router.push(
        //@ts-ignore
        `/trade/${lotteryId}/select-desired-prizes?dataKey=${storageKey}&detailsKey=${detailsKey}&allGoodsKey=${allGoodsKey}`,
      );
    } catch {
      // エラーが発生した場合は通常の遷移を行う
      //@ts-ignore
      router.push(`/trade/${lotteryId}/select-desired-prizes`);
    }
  };

  const handleGoBack = () => {
    // 現在のパスに応じて戻り先を決定
    if (pathname === `/trade/${lotteryId}/select-tradeable-prizes`) {
      // select-tradeable-prizesページの場合は、trade/{lotteryId}に戻る
      //@ts-ignore
      router.push(`/trade/${lotteryId}`);
    } else if (pathname === `/trade/${lotteryId}/select-desired-prizes`) {
      // select-desired-prizesページの場合は、select-tradeable-prizesに戻る
      //@ts-ignore
      router.push(`/trade/${lotteryId}/select-tradeable-prizes`);
    } else {
      //@ts-ignore
      // その他の場合はブラウザの履歴で戻る
      router.back();
    }
  };

  const handleGoToConfirmation = () => {
    // /select-desired-prizes画面での処理
    if (isSelectDesiredPrizesPage && goods) {
      // 選択されたアイテムをin_goods形式で作成
      const inGoods = Array.from(selectedItems.entries())
        .filter(([, count]) => count > 0)
        .map(([goodId, count]) => ({
          lottery_prize_goods_id: Number(goodId),
          count: count,
        }));

      // sessionStorageに保存
      const confirmDataKey = `confirmData_${lotteryId}_${Date.now()}`;
      const confirmData = {
        hash_key: hashKey || 'default-hash-key',
        in_goods: inGoods,
      };

      try {
        sessionStorage.setItem(confirmDataKey, JSON.stringify(confirmData));

        //@ts-ignore
        router.push(`/trade/${lotteryId}/confirm?confirmDataKey=${confirmDataKey}`);
      } catch (error) {
        console.error('Failed to save confirm data:', error);
      }
    }
    // /confirm画面での処理（「交換を確定する」ボタン）
    else if (isConfirmPage) {
      // completeページに渡すデータを準備
      const completeDataKey = `completeData_${lotteryId}_${Date.now()}`;
      const completeData = {
        lottery_event_id: lotteryId,
        hash_key: hashKey || 'default-hash-key',
      };

      try {
        sessionStorage.setItem(completeDataKey, JSON.stringify(completeData));
        //@ts-ignore
        router.push(`/trade/${lotteryId}/complete?completeDataKey=${completeDataKey}`);
      } catch (error) {
        console.error('Failed to save complete data:', error);
        //@ts-ignore
        router.push(`/trade/${lotteryId}/complete`);
      }
    } else {
      //@ts-ignore
      router.push(`/trade/${lotteryId}/complete`);
    }
  };

  const handleCancelClick = () => {
    setIsModalOpen(true);
  };

  const handleConfirmCancel = () => {
    if (!tradeRequestId) return;

    try {
      // tradeRequestIdを配列に詰めてセッションストレージに保存
      const cancelledTradeRequests = [tradeRequestId];
      sessionStorage.setItem('cancelledTradeRequests', JSON.stringify(cancelledTradeRequests));

      // /cancelCompleteページに遷移（API呼び出しはcancelCompleteページで行う）
      //@ts-ignore
      router.push(`/trade/${lotteryId}/history/${tradeRequestId}/cancelComplete`);
    } catch (error) {
      console.error('セッションストレージ保存でエラーが発生しました:', error);
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  return (
    <>
      <div className={styles.componentStyle}>
        <div className={styles.container}>
          {/* history/[tradeRequestId]ページ専用ボタン */}
          {isHistoryDetailPage && (
            <div className={styles.buttonWrapper}>
              <ActionButton
                color='black'
                bgColor='white'
                outlined
                //@ts-ignore
                onClick={() => router.push(`/trade/${lotteryId}/history`)}
              >
                景品交換履歴へ戻る
              </ActionButton>
              <ActionButton color='primary' disabled={!canCancel} onClick={handleCancelClick}>
                景品交換を取り消す
              </ActionButton>
            </div>
          )}

          {/* 通常の取引フローボタン（history/[tradeRequestId]以外で表示） */}
          {!isHistoryDetailPage && (
            <div className={styles.buttonWrapper}>
              <button type='button' className={styles.backButton} onClick={handleGoBack}>
                戻る
              </button>
              {/* select-tradeable-prizesページでは「希望景品の選択へ」ボタンを表示 */}
              {isSelectTradeablePrizesPage && (
                <button
                  type='button'
                  className={styles.confirmButton}
                  onClick={handleNavigateToDesiredPrizes}
                  disabled={hasInsufficientLabelOnTradeablePage}
                >
                  希望景品の選択へ
                </button>
              )}
              {/* select-desired-prizesページでは「確認画面へ」ボタンを表示 */}
              {isSelectDesiredPrizesPage && (
                <button
                  type='button'
                  className={styles.confirmButton}
                  onClick={handleGoToConfirmation}
                  disabled={remainingCount > 0}
                >
                  確認画面へ
                </button>
              )}
              {isConfirmPage && (
                <button type='button' className={styles.confirmButton} onClick={handleGoToConfirmation}>
                  景品を交換する
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      <ModalBase isOpen={isModalOpen} onClose={handleModalClose}>
        <div className={styles.cancelModal}>
          <div className={styles.modalContent}>
            <h1 className={`${styles.modalTitle} ${utilsStyles.textBold}`}>景品交換を取り消す</h1>
            <p className={`${styles.modalMessage} ${utilsStyles.mtSmall} ${utilsStyles.primaryColor}`}>
              ※一度取り消した履歴は戻せません。
            </p>
            <div className={`${styles.modalButtons} ${utilsStyles.mtMedium}`}>
              <ActionButton color='primary' onClick={handleConfirmCancel}>
                はい
              </ActionButton>
              <ActionButton color='white' bgColor='brownishgray' outlined onClick={handleModalClose}>
                いいえ
              </ActionButton>
            </div>
          </div>
        </div>
      </ModalBase>
    </>
  );
}

export default TradeBar;
